import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Compass, ArrowRight, ArrowLeft, ShieldAlert, RotateCcw, FileText, Package as PackageIcon, CheckCircle2 } from "lucide-react";
import { QUESTIONS, computeSuggestion } from "@/lib/advisorLogic";
import { motion, AnimatePresence } from "framer-motion";

const T = {
  pt: {
    overline: "Sugestão de Formulários",
    title: "Qual formulário você precisa?",
    subtitle: "Responda algumas perguntas e o sistema mostra quais formulários USCIS são geralmente preenchidos para o seu cenário. NÃO é análise jurídica — é apenas um mapa técnico determinístico.",
    disclaimer_title: "Não é aconselhamento legal",
    disclaimer_body: "Esta funcionalidade usa regras fixas para mapear seu caso a formulários oficiais. NÃO avaliamos elegibilidade, chances de aprovação, riscos ou estratégia. Consulte um advogado de imigração antes de submeter qualquer petição.",
    start: "Começar",
    restart: "Recomeçar",
    step: "Pergunta {current} de {total}",
    results_title: "Formulários sugeridos",
    results_empty: "Nenhum formulário identificado para essa combinação. Refaça o questionário ou consulte um advogado.",
    reason: "Motivo técnico",
    notes: "Observações",
    package_cta: "Criar pacote automaticamente",
    package_hint: "Detectamos um Pacote Combo compatível com estas respostas.",
    login_to_create: "Faça login para criar os formulários automaticamente",
    open_wizard: "Abrir formulário",
    to_dashboard: "Ir ao painel",
  },
  es: {
    overline: "Sugerencia de Formularios",
    title: "¿Qué formulario necesita?",
    subtitle: "Responda algunas preguntas y el sistema mostrará qué formularios USCIS suelen completarse para su escenario. NO es análisis legal — es solo un mapa técnico determinístico.",
    disclaimer_title: "No es asesoría legal",
    disclaimer_body: "Esta función usa reglas fijas para mapear su caso a formularios oficiales. NO evaluamos elegibilidad, probabilidades de aprobación, riesgos ni estrategia. Consulte a un abogado de inmigración antes de presentar cualquier petición.",
    start: "Comenzar",
    restart: "Reiniciar",
    step: "Pregunta {current} de {total}",
    results_title: "Formularios sugeridos",
    results_empty: "Ningún formulario identificado. Reinicie el cuestionario o consulte a un abogado.",
    reason: "Motivo técnico",
    notes: "Observaciones",
    package_cta: "Crear paquete automáticamente",
    package_hint: "Detectamos un Paquete Combo compatible.",
    login_to_create: "Inicie sesión para crear los formularios automáticamente",
    open_wizard: "Abrir formulario",
    to_dashboard: "Ir al panel",
  },
  en: {
    overline: "Form Advisor",
    title: "Which form do you need?",
    subtitle: "Answer a few questions and the system will show which USCIS forms are usually filed for your scenario. It is NOT legal analysis — just a deterministic technical map.",
    disclaimer_title: "Not legal advice",
    disclaimer_body: "This feature uses fixed rules to map your case to official forms. We do NOT evaluate eligibility, approval chances, risks or strategy. Consult an immigration attorney before submitting any petition.",
    start: "Start",
    restart: "Restart",
    step: "Question {current} of {total}",
    results_title: "Suggested forms",
    results_empty: "No forms identified for this combination. Restart the questionnaire or consult an attorney.",
    reason: "Technical reason",
    notes: "Notes",
    package_cta: "Auto-create package",
    package_hint: "We detected a compatible Combo Package for these answers.",
    login_to_create: "Sign in to auto-create the forms",
    open_wizard: "Open form",
    to_dashboard: "Go to dashboard",
  },
};

export default function FormAdvisorPage() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tr = T[lang] || T.pt;
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(-1); // -1 = intro, otherwise index into visible questions

  const visibleQuestions = useMemo(
    () => QUESTIONS.filter((q) => !q.showIf || q.showIf(answers)),
    [answers],
  );

  const isResults = step >= visibleQuestions.length;
  const currentQuestion = step >= 0 && step < visibleQuestions.length ? visibleQuestions[step] : null;
  const suggestion = useMemo(() => (isResults ? computeSuggestion(answers) : null), [isResults, answers]);

  const handleAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step <= 0) {
      setStep(-1);
      return;
    }
    setStep((s) => s - 1);
  };

  const handleRestart = () => {
    setAnswers({});
    setStep(-1);
  };

  const goCreatePackage = (pkgId) => {
    if (!user) {
      navigate(`/login?redirect=/packages&pkg=${pkgId}`);
    } else {
      navigate(`/packages?pkg=${pkgId}`);
    }
  };

  const goStartForm = () => {
    if (!user) navigate("/login?redirect=/wizard");
    else navigate("/wizard");
  };

  return (
    <div data-testid="advisor-page" className="mx-auto max-w-5xl px-6 py-14">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
            {tr.overline}
          </div>
          <h1 className="font-display mt-3 flex items-center gap-3 text-4xl font-bold tracking-tight sm:text-5xl">
            <Compass className="h-8 w-8 text-[hsl(var(--gold))]" strokeWidth={2} />
            {tr.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {tr.subtitle}
          </p>
        </div>
        {step >= 0 && (
          <Button
            variant="outline"
            onClick={handleRestart}
            data-testid="advisor-restart"
            className="rounded-md"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {tr.restart}
          </Button>
        )}
      </div>

      {/* Disclaimer (persistent) */}
      <div
        data-testid="advisor-disclaimer"
        className="mb-8 flex gap-3 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/[0.08] p-4"
      >
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--warning))]" strokeWidth={2.2} />
        <div>
          <div className="text-sm font-bold text-[hsl(var(--warning))]">{tr.disclaimer_title}</div>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{tr.disclaimer_body}</p>
        </div>
      </div>

      {/* Intro */}
      {step === -1 && (
        <IntroScreen tr={tr} onStart={() => setStep(0)} />
      )}

      {/* Question */}
      {currentQuestion && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion.id + step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.22 }}
            className="relative overflow-hidden rounded-lg border border-border bg-card p-8 shadow-2xl"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))]/60 to-transparent" aria-hidden />
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
              {tr.step.replace("{current}", step + 1).replace("{total}", visibleQuestions.length)}
            </div>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight md:text-3xl">
              {currentQuestion[`label_${lang}`] || currentQuestion.label_pt}
            </h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt.value}
                  data-testid={`advisor-option-${currentQuestion.id}-${opt.value}`}
                  onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                  className="group flex items-center justify-between rounded-md border border-border bg-background px-5 py-4 text-left transition-all hover:border-[hsl(var(--gold))]/60 hover:bg-[hsl(var(--gold))]/5 hover:shadow-lg hover:shadow-[hsl(var(--gold))]/5"
                >
                  <span className="text-sm font-medium">
                    {opt[`label_${lang}`] || opt.label_pt}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-[hsl(var(--gold))]" />
                </button>
              ))}
            </div>
            <div className="mt-8">
              <Button
                variant="ghost"
                onClick={handleBack}
                data-testid="advisor-back"
                className="rounded-md"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {lang === "pt" ? "Voltar" : lang === "es" ? "Atrás" : "Back"}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Results */}
      {isResults && suggestion && (
        <ResultsScreen
          tr={tr}
          suggestion={suggestion}
          lang={lang}
          onCreatePackage={goCreatePackage}
          onOpenWizard={goStartForm}
          onDashboard={() => navigate("/dashboard")}
          user={user}
        />
      )}
    </div>
  );
}

function IntroScreen({ tr, onStart }) {
  return (
    <div
      data-testid="advisor-intro"
      className="relative overflow-hidden rounded-lg border border-border bg-card p-10 text-center shadow-2xl"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))]/60 to-transparent" aria-hidden />
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/10">
        <Compass className="h-6 w-6 text-[hsl(var(--gold))]" />
      </div>
      <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
        {tr.subtitle}
      </p>
      <div className="mt-8">
        <Button
          size="lg"
          data-testid="advisor-start-cta"
          onClick={onStart}
          className="rounded-md bg-[hsl(var(--gold))] px-8 font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
        >
          {tr.start}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ResultsScreen({ tr, suggestion, lang, onCreatePackage, onOpenWizard, onDashboard, user }) {
  const { forms, packageId, notes } = suggestion;
  const hasForms = forms.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="advisor-results"
      className="space-y-6"
    >
      <div className="relative overflow-hidden rounded-lg border border-border bg-card p-8 shadow-2xl">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))]/60 to-transparent" aria-hidden />
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-[hsl(var(--gold))]" strokeWidth={2.2} />
          <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            {tr.results_title}
          </h2>
        </div>

        {!hasForms && (
          <p className="mt-6 text-sm text-muted-foreground">{tr.results_empty}</p>
        )}

        {hasForms && (
          <ul className="mt-6 divide-y divide-border">
            {forms.map((f, idx) => (
              <li
                key={f.code + idx}
                data-testid={`advisor-form-${f.code}`}
                className="flex flex-col gap-2 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-14 items-center justify-center rounded-md border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))]">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-display text-xl font-bold tracking-tight text-[hsl(var(--gold))]">{f.code}</div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {tr.reason}
                    </div>
                  </div>
                </div>
                <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                  {f[`reason_${lang}`] || f.reason_pt}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {notes.length > 0 && (
        <div className="rounded-lg border border-[hsl(var(--sage))]/40 bg-[hsl(var(--sage))]/[0.08] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--sage))]">
            {tr.notes}
          </div>
          <ul className="mt-3 space-y-2 text-sm text-foreground/80">
            {notes.map((n, i) => (
              <li key={i}>— {n[lang] || n.pt}</li>
            ))}
          </ul>
        </div>
      )}

      {hasForms && (
        <div className="flex flex-wrap gap-3">
          {packageId && (
            <div className="flex w-full flex-col gap-3 rounded-lg border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.08] p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <PackageIcon className="h-5 w-5 text-[hsl(var(--gold))]" />
                <div>
                  <div className="font-display text-sm font-semibold text-[hsl(var(--gold))]">{packageId.replace(/_/g, " ")}</div>
                  <p className="text-xs text-muted-foreground">{tr.package_hint}</p>
                </div>
              </div>
              <Button
                data-testid="advisor-create-package"
                onClick={() => onCreatePackage(packageId)}
                className="rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
              >
                {user ? tr.package_cta : tr.login_to_create}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              data-testid="advisor-open-wizard"
              onClick={onOpenWizard}
              className="rounded-md border-[hsl(var(--gold))]/40 bg-transparent text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10 hover:text-[hsl(var(--gold))]"
            >
              {tr.open_wizard}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            {user && (
              <Button
                variant="ghost"
                data-testid="advisor-dashboard"
                onClick={onDashboard}
                className="rounded-md"
              >
                {tr.to_dashboard}
              </Button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
